/**
 * useTranslateWorkflow -- Hook xu ly luong translate.
 *
 * Refactored: Dung utilities (chunkSegments, mergeTranslateResults, mapQCResultToAuditDetails)
 * va workerClient (insertJobs, waitForBatchJobs) thay vi tu viet Realtime subscription.
 *
 * Luong:
 *   1. chunkSegments -> chia source text thanh batches
 *   2. insertJobs -> INSERT vao bang jobs (type='translate')
 *   3. waitForBatchJobs -> doi tat ca batches done via Realtime + polling
 *   4. mergeTranslateResults -> merge ket qua tu pending_sync
 *   5. runJobAndWait(type='quality-check') -> chay QC
 *   6. mapQCResultToAuditDetails -> map QC result sang frontend format
 */

import { useState, useCallback } from 'react';
import { useProjectStore } from '../../../store/useProjectStore';
import { useConfigStore } from '../../../store/useConfigStore';
import { useDebugStore } from '../../../store/useDebugStore';
import { useFileSystemStore } from '../../../store/useFileSystemStore';
import { useJobManager } from '../../../contexts/JobManagerContext';
import { useAuth } from '../../../contexts/AuthContext';
import { insertJobs, waitForBatchJobs, runJobAndWait, runTranslateStream, JobType } from '../../../services/api/workerClient';
import { usePrivacyStore } from '../../../store/usePrivacyStore';
import { fetchAdminDebugLog } from '../../../services/api/adminDebugLog';
import { getLineSegments } from '../../../utils/textUtils';
import { synonymGroupsToFlatText, findMatchingSynonymGroupsForSegments } from '../../../utils/glossaryUtils';
import { buildCompleteParagraphMeta, chunkSegments, chunkSegmentsSmartWithIsolatedRanges, mergeTranslateResults, type BatchChunk } from '../../../utils/batchChunker';
import type { ParagraphDisplay } from '../../../services/api/docxApi';
import type { ParseStructure } from '../../../services/api/docxApi';
import { mapQCResultToAuditDetails } from '../../../utils/qcResultMapper';
import { saveFileImmediately } from '../../../services/shared/saveFileImmediately';
import { logClientError } from '../../../services/shared/logClientError';
import { track } from '../../../services/analytics';

/**
 * Smart chunker (US-17-08) — build paragraph metadata + alignment invariant gate.
 *
 * Returns `aligned: true` only when ALL 4 conditions pass:
 *   1. Every aiIdx across all paragraphs is unique (no duplicates)
 *   2. The aiIdx set is contiguous [0..N-1] (no gaps)
 *   3. N === sourceSegmentsLength (full coverage of segments array)
 *   4. Every paragraph range valid: segIdxStart <= segIdxEnd AND in bounds
 *
 * On misalignment caller should fallback to legacy chunkSegments (no smart chunk attempt).
 */
/**
 * Smart chunker selector — uses chunkSegmentsSmart if metadata aligns, else
 * falls back to legacy chunkSegments. Logs reason on fallback for visibility.
 */
function selectChunker(
    sourceSegments: string[],
    paragraphsDisplay: ParagraphDisplay[] | undefined,
    parseStructure?: ParseStructure,
): BatchChunk[] {
    if (!paragraphsDisplay || paragraphsDisplay.length === 0) {
        return chunkSegments(sourceSegments);
    }
    const result = buildCompleteParagraphMeta(paragraphsDisplay, sourceSegments.length, parseStructure);
    if (!result.aligned) {
        console.warn(`[chunkSmart] metadata misaligned (${result.reason}), fallback to balanced chunker`);
        return chunkSegments(sourceSegments);
    }
    const smartCount = parseStructure?.smartLabels?.length || 0;
    const smartStart = parseStructure?.smartAiStart;
    const isolatedRanges = smartCount > 0 && smartStart !== undefined
        ? [{ startIdx: smartStart, endIdx: smartStart + smartCount - 1 }]
        : [];
    return chunkSegmentsSmartWithIsolatedRanges(sourceSegments, result.meta, isolatedRanges);
}

export const useTranslateWorkflow = () => {
    // Note: files/projects/updateFile are read via useProjectStore.getState() inside callbacks to avoid stale closures
    const { config } = useConfigStore();
    const { addDebugLog } = useDebugStore();
    const { addJob, completeJob, failJob } = useJobManager();
    const { user, isAdmin } = useAuth();

    const [isTranslating, setIsTranslating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTranslatingFileId, setActiveTranslatingFileId] = useState<string | null>(null);
    const [activeTotalSegments, setActiveTotalSegments] = useState(0);
    const [translateProgress, setTranslateProgress] = useState({ completedBatches: 0, totalBatches: 0, percent: 0 });
    // US-04-13 Layer 2: bug report state — when translate fails, expose context for BugReportModal trigger
    const [bugReportContext, setBugReportContext] = useState<{ fileName: string; errorMessage: string; jobId?: string } | null>(null);

    /**
     * Run translate + auto QC cho 1 file.
     */
    const runTranslateJob = useCallback(async (fileId: string) => {
        // US-19-16: ensure file content (sourceText) loaded before translate workflow reads it.
        const { ensureFileContentLoaded } = await import('../../../hooks/useFileSystem');
        await ensureFileContentLoaded(fileId);

        // Always read fresh state from store (avoid stale closure)
        const { files: freshFiles, projects: freshProjects, updateFile: storeUpdateFile } = useProjectStore.getState();
        const file = freshFiles.find(f => f.id === fileId);
        const project = freshProjects.find(p => p.id === file?.projectId);
        if (!file || !project || !user) return;

        setIsTranslating(true);
        setError(null);
        setActiveTranslatingFileId(fileId);

        track('file_translate_start', {
            file_id: fileId,
            project_id: project.id,
            source_lang: project.sourceLang || 'vi',
            target_lang: project.targetLang || 'en',
        });

        const projectConfig = {
            ...config,
            systemInstruction: project.systemPrompt || config.systemInstruction,
            extraRules: synonymGroupsToFlatText(project.importedTerminology || []) || config.extraRules,
            synonymGroups: project.importedTerminology || [],
            protectedPatterns: config.protectedPatterns || [],
            locale: project.locale || config.locale,
            sourceLang: project.sourceLang || 'vi',
            targetLang: project.targetLang || 'en',
            qcOutputLang: project.qcOutputLang || 'vi', // per-project setting, default 'vi'
            excludedRules: file.excludedRules || []
        };

        const sourceSegments = getLineSegments(file.sourceText).map(s => s.text);
        setActiveTotalSegments(sourceSegments.length);

        // Build matched rules string for highlighting in export
        const synonymGroups = project.importedTerminology || [];
        const matchedGroups = findMatchingSynonymGroupsForSegments(file.sourceText, synonymGroups);
        const excludedSet = new Set((file.excludedRules || []).map((r: string) => r.toLowerCase()));
        const matchedRulesText = matchedGroups
            .filter(m => !excludedSet.has(m.match.toLowerCase()))
            .map(m => {
                const defaultTarget = m.group.targets.find(t => t.isDefault)?.text || m.group.targets[0]?.text || '';
                return `"${m.match}" -> "${defaultTarget}"`;
            })
            .join('\n');

        // Clear stale data nhưng KHÔNG set 'running' yet — đợi insertJobs success mới set.
        // Lý do: nếu insertJobs fail (network/credit/rate-limit), file không nên hiện running.
        console.log(`[Translate] Clearing stale data for file=${fileId}, status stays 'pending' until job posted`);
        storeUpdateFile(fileId, {
            aiOutput: [],
            auditStatus: 'pending',
            auditResult: undefined,
            auditRawOutput: undefined,
            auditUsage: undefined,
            matchedRules: matchedRulesText,
            error: undefined,
        });

        try {
            // === 1. Chia batch ===
            // Smart chunker (US-17-08) — uses paragraph metadata to avoid bad cuts.
            // Falls back to balanced chunker if metadata absent or misaligned.
            const batches = selectChunker(sourceSegments, file.backendParagraphs, file.backendParseStructure);
            const totalBatches = batches.length;
            const estimateTokens = (text: string) => Math.ceil(text.length / 3.5);

            console.log(`[Translate] ${sourceSegments.length} segments, ~${estimateTokens(sourceSegments.join('\n'))} tokens => ${totalBatches} batch(es)`);

            const jobManagerId = addJob('translation', fileId, file.fileName);
            addDebugLog('Translation (Jobs Queue)', '', {
                segments: sourceSegments.length,
                batches: totalBatches,
                info: `INSERT ${totalBatches} job(s) -> pg_net fire -> Worker xu ly`,
            }, undefined, fileId);

            // === 2. Snapshot privacy mode at translate start (per-job, not session-wide) ===
            const privacyMode = usePrivacyStore.getState().mode;

            // === 2-3. Branch: incognito = SSE direct, cloud_sync = jobs+pending_sync ===
            // Both paths produce equivalent batchResult shape ({syncRows, doneCount, errorCount})
            // so downstream merge/batchInfo logic stays identical (per US-12-01 contract B-3).
            let batchResult: { syncRows: { batch_index: number; result: any; type?: string; job_id?: string }[]; doneCount: number; errorCount: number; usage: { inputTokens: number; outputTokens: number; cost: number } };
            const sharedRunId = crypto.randomUUID();

            if (privacyMode === 'incognito') {
                // INCOGNITO PATH: SSE per-batch parallel via Promise.all (US-12-01 B-5)
                console.log(`[Translate] INCOGNITO mode — SSE direct stream, run_id=${sharedRunId.slice(0, 8)}...`);

                // ✅ Set running status — SSE streams sẽ tạo job server-side ngay khi POST
                storeUpdateFile(fileId, { translationStatus: 'running', currentStep: 3 });

                type StreamSettleResult =
                    | { ok: true; idx: number; result: { result: any; usage: { inputTokens: number; outputTokens: number; cost: number } } }
                    | { ok: false; idx: number; error: any };
                const streamPromises: Promise<StreamSettleResult>[] = batches.map((b, idx) =>
                    runTranslateStream({
                        type: 'translate' as JobType,
                        fileId,
                        fileName: file.fileName,
                        batchIndex: idx,
                        totalBatches,
                        runId: sharedRunId,
                        privacyMode: 'incognito',
                        input: { segments: b.segments, config: projectConfig },
                    }).then<StreamSettleResult, StreamSettleResult>(
                        (res) => ({ ok: true, idx, result: res }),
                        (err) => ({ ok: false, idx, error: err })
                    )
                );

                // Progress reporter — settle as each batch completes
                let completedBatches = 0;
                const reportProgress = () => {
                    setTranslateProgress({
                        completedBatches,
                        totalBatches,
                        percent: Math.round((completedBatches / Math.max(1, totalBatches)) * 100),
                        currentStatus: completedBatches >= totalBatches ? 'complete' : 'running',
                    });
                };
                reportProgress();
                streamPromises.forEach(p => p.then(() => { completedBatches++; reportProgress(); }));

                const settled = await Promise.all(streamPromises);

                const syncRows: { batch_index: number; result: any; type?: string; job_id?: string }[] = [];
                let inputTokens = 0, outputTokens = 0, cost = 0;
                let doneCount = 0, errorCount = 0;
                for (const s of settled) {
                    if (s.ok) {
                        syncRows.push({ batch_index: s.idx, result: s.result.result, type: 'translate' });
                        inputTokens += s.result.usage.inputTokens || 0;
                        outputTokens += s.result.usage.outputTokens || 0;
                        cost += s.result.usage.cost || 0;
                        doneCount++;
                    } else {
                        const errMsg = (s as { ok: false; idx: number; error: any }).error?.message || 'unknown';
                        console.warn(`[Translate] INCOGNITO batch ${s.idx} failed: ${errMsg}`);
                        errorCount++;
                    }
                }
                if (doneCount === 0) throw new Error('Tat ca batches Ẩn Danh đều thất bại');
                batchResult = { syncRows, doneCount, errorCount, usage: { inputTokens, outputTokens, cost } };
            } else {
                // CLOUD_SYNC PATH (default): unchanged existing flow
                const jobParams = batches.map((b, idx) => ({
                    type: 'translate' as JobType,
                    fileId,
                    fileName: file.fileName,
                    batchIndex: idx,
                    totalBatches,
                    runId: sharedRunId,
                    privacyMode: 'cloud_sync' as const,
                    input: { segments: b.segments, config: projectConfig },
                }));

                // Pattern an toàn: chờ API trả 200 OK (job đã lưu DB) rồi mới flip status='running'.
                // Tránh false positive: nếu API fail (credit/rate-limit/network), UI không kẹt 'running'.
                // Bật dispatch flag → beforeunload warning user nếu refresh/close giữa API + disk window.
                const { useDispatchStore } = await import('../../../store/useDispatchStore');
                useDispatchStore.getState().begin();
                let jobIds: string[];
                try {
                    jobIds = await insertJobs(jobParams);

                    storeUpdateFile(fileId, {
                        translationStatus: 'running',
                        currentStep: 3,
                        translateProgress: {
                            completedBatches: 0,
                            totalBatches: totalBatches,
                            completedSegments: 0,
                            totalSegments: sourceSegments.length,
                        },
                    });

                    // AWAIT disk write trong dispatch window — beforeunload còn active
                    // cho đến khi state.json có status='running'.
                    const { isConnected: isFsConnected, rootHandle: fsRootHandle } = useFileSystemStore.getState();
                    if (isFsConnected && fsRootHandle) {
                        const updatedFile = useProjectStore.getState().files.find(f => f.id === fileId);
                        if (updatedFile) {
                            try {
                                await saveFileImmediately(fsRootHandle, project, updatedFile);
                            } catch (err) {
                                console.warn('[Translate] state save failed (non-blocking):', err);
                            }
                        }
                    }
                } finally {
                    useDispatchStore.getState().end();
                }

                // Read actual run_id from jobs row (insertJobs created its own if not set; we passed sharedRunId)
                const { data: jobRow } = await (await import('../../../services/supabase/supabaseClient')).supabase
                    .from('jobs')
                    .select('run_id')
                    .eq('id', jobIds[0])
                    .limit(1);
                const actualRunId = jobRow?.[0]?.run_id || sharedRunId;

                console.log(`[Translate] ${totalBatches} job(s) inserted, run_id=${actualRunId.slice(0, 8)}...`);

                batchResult = await waitForBatchJobs(
                    actualRunId,
                    totalBatches,
                    (progress) => {
                        setTranslateProgress(progress);
                        // Persist progress trên file → UI mọi running file đọc được, không cần active session check
                        storeUpdateFile(fileId, {
                            translateProgress: {
                                completedBatches: progress.completedBatches,
                                totalBatches: progress.totalBatches,
                                completedSegments: Math.round(progress.percent * sourceSegments.length / 100),
                                totalSegments: sourceSegments.length,
                            },
                        });
                    },
                );
            }

            // === 4. Merge results ===
            // Pass batch sizes để position-aware merge — fix bug "batch done lưu nhầm vị trí"
            // khi có batch lỗi/missing trong giữa.
            const translateRows = batchResult.syncRows.filter(r => r.type === 'translate' || !r.type);
            const batchSizes = batches.map(b => b.segments.length);
            const finalResult = mergeTranslateResults(translateRows, sourceSegments.length, batchSizes);
            const translatedCount = finalResult.filter(Boolean).length;

            console.log(`[Translate] Merged: ${translatedCount}/${sourceSegments.length} segments from ${batchResult.doneCount} batches (${batchResult.errorCount} errors)`);

            // === 4b. Build batchInfo — detect missing/format errors per batch ===
            const batchInfoList: import('../../../types').BatchInfo[] = batches.map((b, idx) => {
                const syncRow = translateRows.find(r => r.batch_index === idx);
                const batchTranslated: string[] = syncRow?.result?.translated || [];
                const expectedCount = b.segments.length;
                // Đếm missing = segments có source text nhưng AI trả empty
                // Chỉ đếm missing khi source KHÔNG rỗng mà translated rỗng
                let missingCount = 0;
                const missingIndices: number[] = [];
                for (let i = 0; i < expectedCount; i++) {
                    const src = b.segments[i]?.trim();
                    const trans = batchTranslated[i]?.trim();
                    if (src && !trans) {
                        missingCount++;
                        missingIndices.push(b.startIdx + i + 1); // 1-indexed global segment #
                    }
                }
                const formatErrorIndices: number[] = [];
                for (let i = 0; i < batchTranslated.length; i++) {
                    if (batchTranslated[i] && batchTranslated[i].includes(' ::: ')) {
                        formatErrorIndices.push(b.startIdx + i + 1); // 1-indexed global segment #
                    }
                }
                const formatErrors = formatErrorIndices.length;

                // Detect shift: AI trả đúng số dòng nhưng nội dung bị lệch
                // Nếu missing chỉ ở cuối batch → khả năng AI gộp dòng ở đầu/giữa → shift xuống
                const isLikelyShift = missingCount > 0 && missingCount <= 3 &&
                    missingIndices.every(idx => idx > b.startIdx + expectedCount - 5); // missing chỉ ở 5 dòng cuối

                let status: 'done' | 'error' | 'cancelled' = syncRow ? (missingCount > 0 || formatErrors > 0 ? 'error' : 'done') : 'cancelled';

                let error: string | undefined;
                const missingDetail = missingIndices.length > 0
                    ? ` (dòng ${missingIndices.length <= 5 ? missingIndices.join(', ') : missingIndices.slice(0, 5).join(', ') + '...'})`
                    : '';
                const formatDetail = formatErrorIndices.length > 0
                    ? ` (dòng ${formatErrorIndices.length <= 5 ? formatErrorIndices.join(', ') : formatErrorIndices.slice(0, 5).join(', ') + '...'})`
                    : '';
                if (missingCount > 0 && formatErrors > 0) {
                    error = `${missingCount} đoạn bị thiếu${missingDetail}, ${formatErrors} sai định dạng${formatDetail}`;
                } else if (missingCount > 0) {
                    const shiftHint = isLikelyShift
                        ? ' — AI có thể đã gộp dòng ở phía trên khiến bản dịch bị lệch'
                        : '';
                    error = `${missingCount}/${expectedCount} đoạn bị thiếu${missingDetail}${shiftHint}`;
                } else if (formatErrors > 0) {
                    error = `${formatErrors} đoạn sai định dạng (:::)${formatDetail}`;
                }

                return {
                    index: idx,
                    startSegment: b.startIdx,
                    endSegment: b.startIdx + expectedCount,
                    segmentCount: expectedCount,
                    status,
                    missingCount: missingCount > 0 ? missingCount : undefined,
                    error,
                    jobId: syncRow?.job_id,  // US-13-04: lưu cho retry skip double-charge
                };
            });

            const failedBatches = batchInfoList.filter(b => b.status !== 'done');
            const totalMissing = batchInfoList.reduce((sum, b) => sum + (b.missingCount || 0), 0);

            // === Debug log per batch (US-19-01 Task 3: admin-only fetch via private debug log table) ===
            if (isAdmin) {
                for (const row of batchResult.syncRows) {
                    if (!row.job_id) continue;
                    fetchAdminDebugLog(row.job_id).then(log => {
                        if (log && (log.prompt_input || log.raw_response)) {
                            addDebugLog(
                                `Translate Batch ${row.batch_index + 1}/${totalBatches}`,
                                '',
                                { userPrompt: log.prompt_input || '(không có)', rawOutput: log.raw_response || undefined },
                                undefined, fileId
                            );
                        }
                    }).catch(() => { /* swallow — best-effort, no UI impact */ });
                }
            }

            const transUsage = {
                ...batchResult.usage,
                totalTokens: batchResult.usage.inputTokens + batchResult.usage.outputTokens,
            };
            completeJob(jobManagerId, transUsage);

            const hasErrors = batchResult.errorCount > 0 || failedBatches.length > 0;
            if (!hasErrors && totalMissing === 0) {
                track('file_translate_done', {
                    file_id: fileId,
                    project_id: project.id,
                    segments_count: sourceSegments.length,
                    input_tokens: batchResult.usage.inputTokens,
                    output_tokens: batchResult.usage.outputTokens,
                    total_tokens: transUsage.totalTokens,
                });
            }

            let errorMsg: string | undefined;
            if (batchResult.errorCount > 0) {
                errorMsg = `${batchResult.errorCount} batch thất bại. ${translatedCount}/${sourceSegments.length} đoạn đã dịch.`;
            } else if (totalMissing > 0) {
                // Gom tất cả missing segment indices
                const allMissingIndices = batchInfoList.flatMap(b => {
                    if (!b.error) return [];
                    const match = b.error.match(/\(dòng\s*(.+?)\)/);
                    return match ? match[1].split(', ').map(s => s.replace('...', '')) : [];
                }).filter(Boolean);
                const missingPreview = allMissingIndices.length > 0 ? ` (dòng ${allMissingIndices.slice(0, 8).join(', ')}${allMissingIndices.length > 8 ? '...' : ''})` : '';
                errorMsg = `${totalMissing} đoạn bị thiếu trong ${failedBatches.length} batch${missingPreview}. Có thể AI đã gộp các dòng ngắn lại. Bấm Retry hoặc xem chi tiết.`;
            }

            // Nếu có kết quả dịch (dù thiếu vài segments), vẫn cho xem Export
            const hasAnyOutput = finalResult.some(s => s && s.trim());
            storeUpdateFile(fileId, {
                translationStatus: hasErrors ? 'error' : 'done',
                aiOutput: finalResult,
                batchInfo: batchInfoList,
                currentStep: (project.type === 'translate' && hasAnyOutput) ? 4 : (hasErrors ? 3 : 4),
                completedAt: new Date(),
                translationUsage: transUsage,
                ...(errorMsg ? { error: errorMsg } : {}),
            });

            // US-19-16 follow-up: persist aiOutput xuống disk ngay khi translate done.
            // Bug pattern trước: completion chỉ update RAM → reload mất bản dịch (state.json
            // strip aiOutput vì US-19-16 lazy-load, .translation.txt không được tạo).
            // Fix: gọi saveFileImmediately để write .translation.txt + .source.txt + .original.docx.
            const fsStateAfterTranslate = useFileSystemStore.getState();
            if (fsStateAfterTranslate.isConnected && fsStateAfterTranslate.rootHandle) {
                const completedFile = useProjectStore.getState().files.find(f => f.id === fileId);
                if (completedFile) {
                    await saveFileImmediately(fsStateAfterTranslate.rootHandle, project, completedFile);
                }
            }

            // === 5. Auto Quality Check (chạy khi file không skipQC) ===
            // Source of truth: file.skipQC toggle UI (TranslateTab "Kiểm tra" checkbox).
            // Dropped config.autoQC global gate (dead config — không có UI toggle, default false → never run).
            // Wrap in own try/catch — QC failure must NOT overwrite translationStatus='done'.
            // QC errors set auditStatus='error' independently inside runQualityCheck.
            const freshFile = useProjectStore.getState().files.find(f => f.id === fileId);
            if (translatedCount > 0 && !freshFile?.skipQC) {
                try {
                    await runQualityCheck(fileId, sourceSegments, finalResult, projectConfig);
                } catch (qcErr: any) {
                    console.error('[Translate] Auto-QC failed (translate result preserved):', qcErr);
                    // Do NOT overwrite translationStatus — translate already succeeded.
                    // runQualityCheck should set auditStatus='error' itself; if not, log only.
                }
            }

        } catch (e: any) {
            console.error('[Translate] Failed:', e);
            setError(e.message || 'Translate failed');
            // US-04-13 Layer 2: capture context for BugReportModal opt-in
            const fileForBug = useProjectStore.getState().files.find(f => f.id === fileId);
            setBugReportContext({
                fileName: fileForBug?.name || 'unknown.docx',
                errorMessage: e.message || 'Translate failed',
                jobId: undefined,
            });
            // Only mark as error if translate didn't already produce output.
            // Defensive: avoid overwriting 'done' status for downstream errors.
            const currentFile = useProjectStore.getState().files.find(f => f.id === fileId);
            if (currentFile?.translationStatus !== 'done') {
                storeUpdateFile(fileId, { translationStatus: 'error', error: e.message });
            }
            // Persist error → Supabase error_logs để debug user issues sau khi xảy ra
            logClientError({
                message: e?.message || 'Translate failed',
                stack: e?.stack,
                action: 'translate',
                inputSummary: `fileId=${fileId} fileName=${currentFile?.fileName ?? 'unknown'} batches=${currentFile?.batchInfo?.length ?? 0}`,
                userId: user?.id ?? null,
            });
        } finally {
            setIsTranslating(false);
            setActiveTranslatingFileId(null);
            setTranslateProgress({ completedBatches: 0, totalBatches: 0, percent: 0 });
        }
    }, [config, user]);

    /**
     * Auto Quality Check sau translate.
     */
    const runQualityCheck = async (
        fileId: string,
        sourceSegments: string[],
        translatedSegments: string[],
        projectConfig: any
    ) => {
        const { files: freshFiles, updateFile: storeUpdate } = useProjectStore.getState();
        const file = freshFiles.find(f => f.id === fileId);
        if (!file) return;

        const qcJobManagerId = addJob('quality-check', fileId, file.fileName);
        storeUpdate(fileId, { auditStatus: 'running', currentStep: 4 });

        try {
            addDebugLog('Quality Check (Jobs Queue)', '', {
                model: projectConfig.auditModel || projectConfig.model,
                segments: translatedSegments.filter(Boolean).length,
                info: 'INSERT job type=quality-check -> pg_net trigger -> Worker',
            }, undefined, fileId);

            const { result: qcResult, usage } = await runJobAndWait({
                type: 'quality-check',
                fileId,
                fileName: file.fileName,
                billingContext: 'qc_bundle',  // US-11-09: charge via QC factor formula = bundle preflight
                input: {
                    sourceSegments,
                    aiSegments: translatedSegments.filter(s => s !== ''),
                    config: projectConfig,
                },
            });

            const auditResult = mapQCResultToAuditDetails(qcResult);
            const qcUsage = {
                ...usage,
                totalTokens: usage.inputTokens + usage.outputTokens,
            };

            completeJob(qcJobManagerId, qcUsage);
            storeUpdate(fileId, {
                auditResult,
                auditScore: auditResult?.overallScore,
                auditStatus: 'done',
                currentStep: 4,
                completedAt: new Date(),
                auditUsage: qcUsage,
            });

            // Eager save .audit.json (bypass 500ms debounce) — tránh mất QC result
            // nếu user hard refresh ngay sau QC xong.
            try {
                const fsStore = (await import('../../../store/useFileSystemStore')).useFileSystemStore.getState();
                const projStore = (await import('../../../store/useProjectStore')).useProjectStore.getState();
                if (fsStore.isConnected && fsStore.rootHandle) {
                    const { sanitizeFolderName, sanitizeFileName } = await import('../../../utils/textUtils');
                    const { fileSystemPersistence } = await import('../../../services/shared/fileSystemPersistence');
                    (fileSystemPersistence as any).handle = fsStore.rootHandle;
                    const proj = projStore.projects.find(p => p.id === file.projectId);
                    if (proj) {
                        const category = proj.type === 'translate' ? 'translating' : proj.type === 'train' ? 'training' : '';
                        const projectFolderName = `project_${sanitizeFolderName(proj.name, proj.id)}`;
                        const safeName = sanitizeFileName(file.fileName);
                        const shortId = file.id.split('-')[0] || file.id.slice(0, 8);
                        const fileBaseName = `${safeName}.${shortId}`;
                        const projectPath = category ? [category, projectFolderName] : [projectFolderName];
                        await fileSystemPersistence.saveJson([...projectPath, 'files'], `${fileBaseName}.audit.json`, auditResult);
                        console.log('[QC] Eager-saved .audit.json to disk');
                    }
                }
            } catch (saveErr: any) {
                console.warn('[QC] Eager save failed (auto-save will retry):', saveErr?.message);
            }

            console.log('[QC] Done, auditResult applied');
        } catch (e: any) {
            console.error('[QC] Failed:', e);
            storeUpdate(fileId, { auditStatus: 'error' });
            failJob(qcJobManagerId, e.message);
        }
    };

    /**
     * Retry mot hoac nhieu batches that bai cua mot file.
     * Giu nguyen aiOutput cua cac batch da done, chi dich lai batch duoc chi dinh.
     */
    const retryBatches = useCallback(async (fileId: string, batchIndices: number[]) => {
        // US-19-16: ensure full content loaded before retry reads sourceText
        const { ensureFileContentLoaded } = await import('../../../hooks/useFileSystem');
        await ensureFileContentLoaded(fileId);

        const { files: freshFiles, projects: freshProjects, updateFile: storeUpdateFile } = useProjectStore.getState();
        const file = freshFiles.find(f => f.id === fileId);
        const project = freshProjects.find(p => p.id === file?.projectId);
        if (!file || !project || !user || batchIndices.length === 0) return;

        // Khong set isTranslating/activeTranslatingFileId — tranh hien "Translating" o header
        // va progress bar toan file. UI chi can reload o batch cu the (qua batchInfo.status).
        setError(null);

        const projectConfig = {
            ...config,
            systemInstruction: project.systemPrompt || config.systemInstruction,
            extraRules: synonymGroupsToFlatText(project.importedTerminology || []) || config.extraRules,
            synonymGroups: project.importedTerminology || [],
            protectedPatterns: config.protectedPatterns || [],
            locale: project.locale || config.locale,
            sourceLang: project.sourceLang || 'vi',
            targetLang: project.targetLang || 'en',
            qcOutputLang: project.qcOutputLang || 'vi', // per-project setting, default 'vi'
            excludedRules: file.excludedRules || []
        };

        const sourceSegments = getLineSegments(file.sourceText).map(s => s.text);

        // Smart chunker (US-17-08) — same selection as primary translate path
        // so retry batch indices align with original chunking.
        const allBatches = selectChunker(sourceSegments, file.backendParagraphs, file.backendParseStructure);
        const batchesToRetry = batchIndices
            .map(idx => ({ idx, batch: allBatches[idx] }))
            .filter(x => x.batch);

        if (batchesToRetry.length === 0) return;

        console.log(`[RetryBatches] file=${fileId}, batches=[${batchesToRetry.map(b => b.idx).join(',')}]`);

        // Mark retried batches as 'running' trong batchInfo (giu nguyen translationStatus='error'
        // de UI Export khong bi an — user van thay tab xuat file va tien trinh retry tai panel batch).
        const retrySet = new Set(batchesToRetry.map(b => b.idx));
        const inProgressBatchInfo: import('../../../types').BatchInfo[] = (file.batchInfo || []).map(b =>
            retrySet.has(b.index) ? { ...b, status: 'running' as const, error: undefined, missingCount: undefined } : b
        );
        storeUpdateFile(fileId, {
            batchInfo: inProgressBatchInfo,
        });

        const jobManagerId = addJob('translation', fileId, `${file.fileName} (retry ${batchesToRetry.length})`);

        try {
            const runId = crypto.randomUUID();
            const jobParams = batchesToRetry.map(({ idx, batch }, i) => {
                // US-13-04: pass retryOf để Worker skip credit deduct (parent đã charged)
                const parentBatchInfo = file.batchInfo?.find(b => b.index === idx);
                return {
                    type: 'translate' as JobType,
                    fileId,
                    fileName: `[RETRY] ${file.fileName}`,
                    batchIndex: idx,
                    totalBatches: allBatches.length,
                    input: { segments: batch.segments, config: projectConfig, isRetry: true },
                    runId: i === 0 ? runId : undefined,
                    retryOf: parentBatchInfo?.jobId,
                };
            });

            await insertJobs(jobParams);

            const batchResult = await waitForBatchJobs(runId, batchesToRetry.length);

            const translateRows = batchResult.syncRows.filter(r => r.type === 'translate' || !r.type);

            // Merge retried batch results vao aiOutput hien co (giu nguyen cac batch da done)
            const existingOutput = [...(file.aiOutput || [])];
            while (existingOutput.length < sourceSegments.length) existingOutput.push('');

            const existingBatchInfo = file.batchInfo || [];
            const updatedBatchInfo: import('../../../types').BatchInfo[] = [...existingBatchInfo];

            for (const { idx, batch } of batchesToRetry) {
                const syncRow = translateRows.find(r => r.batch_index === idx);
                const batchTranslated: string[] = syncRow?.result?.translated || [];
                const expectedCount = batch.segments.length;

                let missingCount = 0;
                const missingIndices: number[] = [];
                for (let i = 0; i < expectedCount; i++) {
                    const src = batch.segments[i]?.trim();
                    const trans = batchTranslated[i]?.trim();
                    if (src && !trans) {
                        missingCount++;
                        missingIndices.push(batch.startIdx + i + 1);
                    }
                    if (syncRow) {
                        existingOutput[batch.startIdx + i] = batchTranslated[i] || '';
                    }
                }
                const formatErrorIndices: number[] = [];
                for (let i = 0; i < batchTranslated.length; i++) {
                    if (batchTranslated[i] && batchTranslated[i].includes(' ::: ')) {
                        formatErrorIndices.push(batch.startIdx + i + 1);
                    }
                }
                const formatErrors = formatErrorIndices.length;

                let status: 'done' | 'error' | 'cancelled' = syncRow ? (missingCount > 0 || formatErrors > 0 ? 'error' : 'done') : 'cancelled';

                let batchError: string | undefined;
                const missingDetail = missingIndices.length > 0
                    ? ` (dòng ${missingIndices.length <= 5 ? missingIndices.join(', ') : missingIndices.slice(0, 5).join(', ') + '...'})`
                    : '';
                const formatDetail = formatErrorIndices.length > 0
                    ? ` (dòng ${formatErrorIndices.length <= 5 ? formatErrorIndices.join(', ') : formatErrorIndices.slice(0, 5).join(', ') + '...'})`
                    : '';
                if (missingCount > 0 && formatErrors > 0) {
                    batchError = `${missingCount} đoạn bị thiếu${missingDetail}, ${formatErrors} sai định dạng${formatDetail}`;
                } else if (missingCount > 0) {
                    batchError = `${missingCount}/${expectedCount} đoạn bị thiếu${missingDetail}`;
                } else if (formatErrors > 0) {
                    batchError = `${formatErrors} đoạn sai định dạng (:::)${formatDetail}`;
                }

                const newInfo: import('../../../types').BatchInfo = {
                    index: idx,
                    startSegment: batch.startIdx,
                    endSegment: batch.startIdx + expectedCount,
                    segmentCount: expectedCount,
                    status,
                    missingCount: missingCount > 0 ? missingCount : undefined,
                    error: batchError,
                    jobId: syncRow?.job_id,  // US-13-04: giữ jobId mới nếu retry lần nữa
                };

                const existingIdx = updatedBatchInfo.findIndex(b => b.index === idx);
                if (existingIdx >= 0) {
                    updatedBatchInfo[existingIdx] = newInfo;
                } else {
                    updatedBatchInfo.push(newInfo);
                }
            }

            // US-19-01 Task 3: admin-only fetch debug via private endpoint
            if (isAdmin) {
                for (const row of batchResult.syncRows) {
                    if (!row.job_id) continue;
                    fetchAdminDebugLog(row.job_id).then(log => {
                        if (log && (log.prompt_input || log.raw_response)) {
                            addDebugLog(
                                `Translate Batch ${row.batch_index + 1}/${allBatches.length} (retry)`,
                                '',
                                { userPrompt: log.prompt_input || '(không có)', rawOutput: log.raw_response || undefined },
                                undefined, fileId
                            );
                        }
                    }).catch(() => { /* swallow */ });
                }
            }

            const transUsage = {
                ...batchResult.usage,
                totalTokens: batchResult.usage.inputTokens + batchResult.usage.outputTokens,
            };
            completeJob(jobManagerId, transUsage);

            const prevUsage = file.translationUsage || { inputTokens: 0, outputTokens: 0, cost: 0, totalTokens: 0 };
            const mergedUsage = {
                inputTokens: (prevUsage.inputTokens || 0) + transUsage.inputTokens,
                outputTokens: (prevUsage.outputTokens || 0) + transUsage.outputTokens,
                cost: (prevUsage.cost || 0) + transUsage.cost,
                totalTokens: (prevUsage.totalTokens || 0) + transUsage.totalTokens,
            };

            const hasErrors = updatedBatchInfo.some(b => b.status !== 'done');
            const totalMissing = updatedBatchInfo.reduce((sum, b) => sum + (b.missingCount || 0), 0);
            const failedBatches = updatedBatchInfo.filter(b => b.status !== 'done');
            let errorMsg: string | undefined;
            if (totalMissing > 0) {
                errorMsg = `${totalMissing} đoạn bị thiếu trong ${failedBatches.length} batch. Có thể AI đã gộp các dòng ngắn lại. Bấm Retry hoặc xem chi tiết.`;
            }

            storeUpdateFile(fileId, {
                translationStatus: hasErrors ? 'error' : 'done',
                aiOutput: existingOutput,
                batchInfo: updatedBatchInfo,
                currentStep: 4,
                completedAt: new Date(),
                translationUsage: mergedUsage,
                ...(errorMsg ? { error: errorMsg } : { error: undefined }),
            });

            // Persist ngay vao disk (mount folder) de khong mat ket qua khi refresh
            const { isConnected, rootHandle } = useFileSystemStore.getState();
            if (isConnected && rootHandle) {
                const updatedFile = useProjectStore.getState().files.find(f => f.id === fileId);
                if (updatedFile) {
                    await saveFileImmediately(rootHandle, project, updatedFile);
                }
            }
        } catch (e: any) {
            console.error('[RetryBatches] Failed:', e);
            setError(e.message || 'Retry failed');
            failJob(jobManagerId, e.message);

            // Revert retried batches từ 'running' (đặt khi click retry) về 'error'
            // với error message mới — nếu không UI sẽ kẹt ở trạng thái 'running' giả.
            const currentFile = useProjectStore.getState().files.find(f => f.id === fileId);
            const revertedBatchInfo = (currentFile?.batchInfo || []).map(b =>
                retrySet.has(b.index) ? { ...b, status: 'error' as const, error: e.message || 'Retry failed' } : b
            );
            storeUpdateFile(fileId, {
                translationStatus: 'error',
                error: e.message,
                batchInfo: revertedBatchInfo,
            });

            const failedFile = useProjectStore.getState().files.find(f => f.id === fileId);
            logClientError({
                message: e?.message || 'Retry failed',
                stack: e?.stack,
                action: 'retry_batches',
                inputSummary: `fileId=${fileId} fileName=${failedFile?.fileName ?? 'unknown'} retryBatches=${batchIndices.length}`,
                userId: user?.id ?? null,
            });

            // Re-throw để caller (App.tsx / CommercialApp.tsx) hiển thị toast/alert.
            throw e;
        }
    }, [config, user]);

    /**
     * Run translate cho tat ca files pending trong project.
     */
    const runAllTranslate = useCallback(async (projectId: string) => {
        const { files: freshFiles, projects: freshProjects } = useProjectStore.getState();
        const project = freshProjects.find(p => p.id === projectId);
        const translateFiles = freshFiles.filter(f =>
            f.projectId === projectId &&
            !f.humanBenchmark && !project?.humanBenchmark &&
            (f.translationStatus === 'pending' || f.translationStatus === 'error')
        );

        for (const file of translateFiles) {
            await runTranslateJob(file.id);
        }
    }, [runTranslateJob]);

    return {
        runTranslateJob,
        runAllTranslate,
        runQualityCheck,
        retryBatches,
        isTranslating,
        error,
        // US-04-13 Layer 2: bug report opt-in trigger
        bugReportContext,
        clearBugReportContext: () => setBugReportContext(null),
        // Progress data (thay the realtimeProgress)
        realtimeProgress: {
            totalTranslated: Math.round(translateProgress.percent * activeTotalSegments / 100),
            totalSegments: activeTotalSegments,
            completedBatches: translateProgress.completedBatches,
            totalBatches: translateProgress.totalBatches,
            percent: translateProgress.percent,
        },
        realtimeBatches: [],  // khong con can chi tiet tung batch
        activeTranslatingFileId,
        activeTotalSegments,
        streamingText: '',  // legacy compat
    };
};
